import React, { useCallback, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Slider,
  TextField,
  Typography
} from "@mui/material";
import { useReportsActions } from "store";
import { SearchImageResult } from "store/types";

interface BackgroundDrawerProps {
  open: boolean;
  currentBackgroundUrl: string | null;
  currentOpacity: number;
  onClose: () => void;
  onApply: (url: string, opacity: number, file: File | null) => void;
  onRemove: () => void;
}

export const BackgroundDrawer: React.FC<BackgroundDrawerProps> = ({
  open,
  currentBackgroundUrl,
  currentOpacity,
  onClose,
  onApply,
  onRemove
}) => {
  const { searchBackgroundImagesAction } = useReportsActions();

  const [backgroundUrl, setBackgroundUrl] = useState(currentBackgroundUrl || "");
  const [backgroundOpacity, setBackgroundOpacity] = useState(currentOpacity);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchImageResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setBackgroundUrl(currentBackgroundUrl || "");
      setBackgroundOpacity(currentOpacity);
      setBackgroundFile(null);
    }
  }, [open, currentBackgroundUrl, currentOpacity]);

  const handleSearchImages = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setSearchLoading(true);
    try {
      const data = await searchBackgroundImagesAction(searchQuery);
      setSearchResults(data);
    } catch (error) {
      console.error("Failed to search images:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, searchBackgroundImagesAction]);

  const handleSelectImage = useCallback((imageUrl: string) => {
    setBackgroundUrl(imageUrl);
    setBackgroundFile(null); // Clear file when selecting from Unsplash
  }, []);

  const handleApply = useCallback(() => {
    onApply(backgroundUrl, backgroundOpacity, backgroundFile);
  }, [backgroundUrl, backgroundOpacity, backgroundFile, onApply]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 400,
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "white" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "16px",
            bgcolor: "#edf1f9",
            color: "#0a3142"
          }}
        >
          <Typography sx={{ fontSize: "17px", fontWeight: 500 }}>Choose a background image</Typography>
          <IconButton
            size="sm"
            onClick={onClose}
            sx={{
              color: "#0a3142",
              "&:hover": { transform: "scale(1.1)" },
              transition: "transform 0.2s"
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, p: "16px", overflow: "auto" }}>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Search, then click an image to set it as this page&apos;s background, or drag one onto the canvas.
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. forest, skyline, abstract"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearchImages();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "24px"
              }
            }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearchImages}
            disabled={searchLoading || !searchQuery.trim()}
            sx={{
              borderRadius: "24px",
              py: 1.5,
              bgcolor: "#173c4c",
              "&:hover": {
                bgcolor: "#0a3142"
              }
            }}
          >
            {searchLoading ? <CircularProgress size={20} color="inherit" /> : "Search"}
          </Button>
          {!!searchResults.length && (
            <Box>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                Search Results
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1.5
                }}
              >
                {searchResults.map((image) => (
                  <Box
                    key={image.regular}
                    onClick={() => handleSelectImage(image.regular)}
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: "8px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: backgroundUrl === image.regular ? "3px solid #173c4c" : "1px solid #e0e0e0",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: 2
                      }
                    }}
                  >
                    <img
                      src={image.thumb}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Divider sx={{ borderColor: "#dae0e2" }} />
          {/* File upload */}
          <Button variant="outlined" component="label" fullWidth startIcon={<ImageIcon />}>
            Upload from computer
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setBackgroundFile(file);
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setBackgroundUrl(event.target?.result as string);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </Button>
          {/* Preview with hover overlay for remove */}
          {backgroundUrl && (
            <Box>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
                Preview
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 160,
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #e0e0e0",
                  "&:hover .remove-overlay": {
                    opacity: 1
                  }
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${backgroundUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: backgroundOpacity / 100
                  }}
                />
                {/* Remove overlay - only show if there's a current background */}
                {currentBackgroundUrl && (
                  <Box
                    className="remove-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: "rgba(0, 0, 0, 0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                      cursor: "pointer"
                    }}
                    onClick={onRemove}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5
                      }}
                    >
                      <IconButton
                        sx={{
                          bgcolor: "white",
                          "&:hover": {
                            bgcolor: "#f8fafc"
                          }
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                      <Typography variant="caption" sx={{ color: "white", fontWeight: 500 }}>
                        Remove background
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {/* Opacity slider */}
          <Box>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
              Opacity: {backgroundOpacity}%
            </Typography>
            <Slider
              value={backgroundOpacity}
              onChange={(_, value) => setBackgroundOpacity(value as number)}
              min={10}
              max={100}
              step={5}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
              sx={{
                color: "#C0CACEFF",
                "& .MuiSlider-thumb": {
                  bgcolor: "#C0CACEFF"
                }
              }}
            />
          </Box>
        </Box>
        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            px: 3,
            py: 2,
            boxShadow: "0 -1px 5px 1px rgba(0, 0, 0, 0.08)"
          }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={!backgroundUrl}>
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
